import type { Placement } from '@popperjs/core';

type CalculateOffsetOptions = {
  placement: Placement;
  rootElement: HTMLDivElement;
  boundaryElement: HTMLElement;
  popperElement: HTMLDivElement;
};

const BOUNDARY_PADDING_OFFSET = 10;

const getStylePxAsNumber = (value: string) => {
  return parseFloat(value.replace('px', ''));
};

const calculateVerticalOffset = (
  rootElement: HTMLDivElement,
  placement: Placement,
) => {
  const { borderTopWidth, borderBottomWidth } = getComputedStyle(rootElement);

  if (placement.startsWith('top')) {
    return getStylePxAsNumber(borderTopWidth);
  }

  return getStylePxAsNumber(borderBottomWidth);
};

const calculateHorizontalOffset = ({
  popperElement,
  boundaryElement,
}: {
  popperElement: HTMLDivElement;
  boundaryElement: HTMLElement;
}) => {
  // Subtract the X component of the currently applied transform so we measure
  // the element's natural CSS position rather than the already-corrected one.
  // getComputedStyle returns the transform as a matrix, e.g. "matrix(1, 0, 0, 1, -61.25, 40.47)".
  // DOMMatrix.m41 is the translateX value (5th parameter in the matrix notation).
  const computedTransform = getComputedStyle(popperElement).transform;
  const currentTransformX =
    computedTransform === 'none' ? 0 : new DOMMatrix(computedTransform).m41;

  const popperRect = popperElement.getBoundingClientRect();
  const naturalLeft = popperRect.left - currentTransformX;
  const naturalRight = naturalLeft + popperRect.width;

  const boundaryRect = boundaryElement.getBoundingClientRect();
  const overflowRight =
    naturalRight - (boundaryRect.right - BOUNDARY_PADDING_OFFSET);
  const overflowLeft =
    boundaryRect.left + BOUNDARY_PADDING_OFFSET - naturalLeft;

  if (overflowRight > 0) {
    return -overflowRight;
  }

  if (overflowLeft > 0) {
    return overflowLeft;
  }

  return 0;
};

export const calculateOffset = ({
  placement,
  rootElement,
  boundaryElement,
  popperElement,
}: CalculateOffsetOptions) => {
  const horizontalOffset = calculateHorizontalOffset({
    popperElement,
    boundaryElement,
  });

  const verticalOffset = calculateVerticalOffset(rootElement, placement);

  return [horizontalOffset, verticalOffset];
};
